import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 14c — access scoping hooks.
 *
 * Thin wrappers over the owner-only `set_node_access` / `set_table_access` tools
 * and the `list_my_access` audit. Mutations invalidate the tree/children/node
 * caches so the 🔒 lock icon and filtered listings refresh immediately. The
 * worker is the source of truth — it validates membership and the no-widen rule
 * and returns any `_warnings`.
 */

export interface AccessScope {
  allowed_users: string[];
}

export interface ToolWarning {
  kind: string;
  message: string;
  blocked?: boolean;
  requires_confirm?: boolean;
}

export interface SetAccessResult {
  node_id: string;
  access_scope: AccessScope | null;
  _warnings?: ToolWarning[];
}

export interface MyAccessSummary {
  user_id: string;
  is_owner: boolean;
  restricted_visible: Array<{ id: string; name: string; type: string; allowed_users: string[] }>;
  restricted_hidden_count: number;
  hidden_by_type: Record<string, number>;
  restricted_total_by_type: Record<string, number>;
}

function errorText(result: { isError?: boolean; content?: unknown }, fallback: string): string {
  return Array.isArray(result.content)
    ? result.content.map((c: { text?: string }) => c.text).join("") || fallback
    : fallback;
}

/** Invalidate every cache that renders node access state. */
function invalidateAccessCaches(qc: ReturnType<typeof useQueryClient>): void {
  for (const key of [
    ["library-tree"],
    ["library-children"],
    ["library-node"],
    ["list-children"],
    ["child-nodes"],
    ["workspace-pages"],
  ]) {
    qc.invalidateQueries({ queryKey: key });
  }
}

/** Owner-only: restrict (or clear) a library node's access scope. */
export function useSetNodeAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      node_id: string;
      allowed_users: string[] | null;
    }): Promise<SetAccessResult> => {
      const result = await mcpClient.toolsCall<SetAccessResult>("set_node_access", {
        node_id: params.node_id,
        allowed_users: params.allowed_users,
      });
      if (result.isError) throw new Error(errorText(result, "Failed to update access"));
      const data = (result.structuredContent ?? {}) as SetAccessResult;
      const warnings = (result as { _warnings?: ToolWarning[] })._warnings ?? data._warnings;
      return { ...data, _warnings: warnings };
    },
    onSuccess: () => invalidateAccessCaches(qc),
  });
}

/** Owner-only: restrict (or clear) a whole table (all its rows) by entity type slug. */
export function useSetTableAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      entity_type_slug: string;
      allowed_users: string[] | null;
    }): Promise<SetAccessResult> => {
      const result = await mcpClient.toolsCall<SetAccessResult>("set_table_access", {
        entity_type_slug: params.entity_type_slug,
        allowed_users: params.allowed_users,
      });
      if (result.isError) throw new Error(errorText(result, "Failed to update table access"));
      const data = (result.structuredContent ?? {}) as SetAccessResult;
      const warnings = (result as { _warnings?: ToolWarning[] })._warnings ?? data._warnings;
      return { ...data, _warnings: warnings };
    },
    onSuccess: () => {
      invalidateAccessCaches(qc);
      qc.invalidateQueries({ queryKey: ["entity-types"] });
    },
  });
}

/** Owner-only: change (or clear) the owning agent of a table. */
export function useChangeTableOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      entity_type_slug: string;
      owner_agent_id: string | null;
    }) => {
      const result = await mcpClient.toolsCall("set_table_owner", {
        entity_type_slug: params.entity_type_slug,
        owner_agent_id: params.owner_agent_id,
      });
      if (result.isError) throw new Error(errorText(result, "Failed to change owner"));
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entity-types"] });
      qc.invalidateQueries({ queryKey: ["library-tree"] });
      qc.invalidateQueries({ queryKey: ["table-nodes"] });
    },
  });
}

/** Audit: what restricted content a user can see, and how much is hidden from them. */
export function useMyAccess(userId?: string) {
  return useQuery({
    queryKey: ["my-access", userId ?? "self"],
    queryFn: async (): Promise<MyAccessSummary | null> => {
      const result = await mcpClient.toolsCall<MyAccessSummary>(
        "list_my_access",
        userId ? { user_id: userId } : {},
      );
      if (result.isError) return null;
      return (result.structuredContent ?? null) as MyAccessSummary | null;
    },
    staleTime: 30_000,
  });
}
